/* eslint-disable sort-keys-fix/sort-keys-fix */
import { type JSX, ReactNode } from 'react';

import { MDText } from 'i18n-react';

import { CalendarIcon, InfoIcon, LocationIcon, PhoneIcon } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { formatPhoneNumber } from 'Utilities/helpers';

import { Tooltip } from 'Storybook/components/local/Tooltip/Tooltip';

import { STATUS_FOR_DISPLAY_COLLECTION_DATE_CONTENT, STATUSES_FOR_DISPLAY_SCHEDULED_COLLECTION_DATE } from '../constants/collectionPageAccessStatuses';
import { useIsDealerDirect } from '../hooks/useIsDealerDirect';
import { useSaleDetailsSub } from '../hooks/useSaleDetailsSub';
import { useVehicleCollectionStatusSub } from '../hooks/useVehicleCollectionStatusSub';
import { useVehicleCollectionSub } from '../hooks/useVehicleCollectionSub';
import { formatDate, formatTime } from '../utils/datetime';
import { COLLECTION_DETAILS_STATUSES_MAP } from '../VehicleCollectionSubjects';

import LocalTexts from './CollectionDetails.texts.json';

import styles from './CollectionDetails.scss';

const LocalT = new MDText(LocalTexts);

type CollectionListItem = {
  content: ReactNode;
  icon: JSX.Element;
  id: string;
  shouldDisplay: boolean;
  title: ReactNode;
}

const datePreferencesToDisplayFormat = (datePreferences?: string[]) => datePreferences?.map((datePreference: string) => formatDate(datePreference, 'DD MMM')).join(', ');

export const scheduledCollectionDateTitle = (
  collectionDate: string | Date,
  vehicleCollectionStatus: string,
) => {
  if (!collectionDate) {
    return '';
  }

  const { COLLECTION_COMPLETED, COLLECTION_DAY } = COLLECTION_DETAILS_STATUSES_MAP;

  if (vehicleCollectionStatus === COLLECTION_COMPLETED) {
    return LocalT.translate('scheduledCollectionDate.contentForCollectionCompleted', {
      date: formatDate(collectionDate),
      time: formatTime(collectionDate),
    });
  }

  if (vehicleCollectionStatus === COLLECTION_DAY) {
    return LocalT.translate('scheduledCollectionDate.contentForCollectionDay');
  }

  return formatDate(collectionDate);
};

export const CollectionDetailsList = (props: { items: CollectionListItem[]; title?: React.ReactNode }) => {
  const { items, title = LocalT.translate('title') } = props;

  return (
    <div className={styles.collectionDetails}>
      {title ? <h2 className={styles.title}>{title}</h2> : null}
      <ul className={styles.collectionDetailsList}>
        {items.map((item) => {
          if (!item.shouldDisplay) {
            return null;
          }

          return (
            <li key={item.id} className={styles.collectionDetailsItem}>
              <span className={styles.iconWrapper}>{item.icon}</span>
              <span className={styles.infoWrapper}>
                <div>{item?.title}</div>
                <div>{item?.content}</div>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const useGetCollectionAddress = (): {
  line1: string;
  line2: string;
} => {
  const { collectionDetails, offer } = useVehicleCollectionSub();
  const isDealerDirect = useIsDealerDirect();

  { // use private scope, so line1, line2 below doesn't clash
    const collectionAddress = collectionDetails?.data?.collection?.sellerContactDetails?.address;
    const { city, county, line1: address1, postcode, street } = collectionAddress || {};

    const line1 = street ?? address1 ?? '';
    const line2 = [county, city, postcode].filter(Boolean).join(', ');
    const hasAddressFromCollection = Boolean(line2);

    // Ensure that we don't mix addresses if enquiry is in move
    if (hasAddressFromCollection || !isDealerDirect) {
      return {
        line1,
        line2,
      };
    }
  }

  {
    const sellerAddress = offer?.data?.seller;
    const { address1, address2, address3, PostCode: postCode, town } = sellerAddress || {};

    const line1 = address1 ?? '';
    const line2 = [address2, address3, postCode, town].filter(Boolean).join(', ');

    return {
      line1,
      line2,
    };
  }
};

export const useGetCollectionPhoneNumber = (): string => {
  const { collectionDetails, offer } = useVehicleCollectionSub();

  const phoneNumberFromCollection = collectionDetails?.data?.collection?.sellerContactDetails?.phoneNumber;
  const phoneNumberFromProfile = offer?.data?.seller?.Mobile;

  const phoneNumber = phoneNumberFromCollection || phoneNumberFromProfile || '';

  return formatPhoneNumber(phoneNumber).pretty || formatPhoneNumber(phoneNumber).number || '';
};

export const useGetDirectAddress = (): CollectionListItem => {
  const { dealershipName } = useSaleDetailsSub();
  const { isSaleComplete, saleCompleteAt } = useVehicleCollectionSub();
  const isDealerDirect = useIsDealerDirect();

  const shouldDisplayDirectAddress = Boolean(isDealerDirect && dealershipName && isSaleComplete && saleCompleteAt);

  return {
    icon: <CalendarIcon />,
    id: 'directDetails',
    shouldDisplay: shouldDisplayDirectAddress,
    content: LocalT.translate('directAddress.content', { dealer: dealershipName }),
    title: formatDate(saleCompleteAt!, 'ddd, D MMMM YYYY'),
  };
};

export const CollectionDetails = () => {
  const { collectionDetails, collectionPreferences, isSaleComplete } = useVehicleCollectionSub();
  const vehicleCollectionStatus = useVehicleCollectionStatusSub();
  const { COLLECTION_PREFERENCES_COMPLETED } = COLLECTION_DETAILS_STATUSES_MAP;

  const { line1, line2 } = useGetCollectionAddress();
  const phoneNumber = useGetCollectionPhoneNumber();
  const isDealerDirect = useIsDealerDirect();

  const scheduledCollectionDate = collectionDetails?.data?.collection?.scheduledCollectionDate ?? '';
  const collectedAt = collectionDetails?.data?.collection?.collectedAt ?? '';
  const collectionDate = collectedAt || scheduledCollectionDate;

  const isCollectionPreferencesCompleted = vehicleCollectionStatus === COLLECTION_PREFERENCES_COMPLETED;
  const shouldDisplayLoadingIndicator = collectionDetails?.loading || collectionPreferences?.loading;

  const shouldDisplayScheduledCollectionDate = (
    collectionDate
    && !isDealerDirect
    && STATUSES_FOR_DISPLAY_SCHEDULED_COLLECTION_DATE.includes(vehicleCollectionStatus)
  );

  const shouldDisplayScheduledCollectionDateContent = (
    collectionDate
    && !isDealerDirect
    && STATUS_FOR_DISPLAY_COLLECTION_DATE_CONTENT.includes(vehicleCollectionStatus)
  );

  const detailItems = {
    directAddress: useGetDirectAddress(),
    scheduledCollectionDate: {
      content: shouldDisplayScheduledCollectionDateContent && LocalT.translate('scheduledCollectionDate.content'),
      icon: <CalendarIcon />,
      shouldDisplay: Boolean(shouldDisplayScheduledCollectionDate),
      title: scheduledCollectionDateTitle(collectionDate, vehicleCollectionStatus),
      id: 'scheduledCollectionDate',
    },
    collectionDateRequested: {
      content: datePreferencesToDisplayFormat(collectionPreferences?.datePreferencesState?.datePreferences),
      icon: <CalendarIcon />,
      shouldDisplay: Boolean(
        isCollectionPreferencesCompleted && collectionPreferences?.datePreferencesState?.datePreferences?.length,
      ),
      title: LocalT.translate('collectionDateRequested.title'),
      id: 'collectionDateRequested',
    },
    collectionAddress: {
      content: line2,
      icon: <LocationIcon filled={false} />,
      shouldDisplay: Boolean(line2),
      title: line1,
      id: 'collectionAddress',
    },
    collectionNumber: {
      content: LocalT.translate('collectionNumber.content'),
      icon: <PhoneIcon />,
      title: (
        <div className={styles.phoneNumberWrapper}>
          <span>{phoneNumber}</span>
          <Tooltip
            className={styles.tooltip}
            maxWidth={245}
            overlay={<div>{LocalT.translate('collectionNumber.tooltip')}</div>}
            placement='top'
          >
            <InfoIcon filled={false} size="xsmall" />
          </Tooltip>
        </div>
      ),
      id: 'collectionPhoneNumber',
      shouldDisplay: Boolean(phoneNumber && !isSaleComplete),
    },
  };

  if (shouldDisplayLoadingIndicator) {
    return <div className={styles.loadingIndicator}><LoadingIndicator size="medium" variant='arrow' /></div>;
  }

  return <CollectionDetailsList items={Object.values(detailItems)} />;
};
